/**
 * Target Discovery leaderboard.
 *
 * Four tabs, no router: **This run** (the selected run's header plus its ranked
 * targets), **Board** (the cumulative cross-run board), **Ask** (questions about
 * finished runs, answered by the read-only analyst) and **Wiki** (what every
 * column and value means). A router would buy deep-linkable URLs at the cost of a
 * dependency and the CloudFront 403/404 rewrite already in place; add one when
 * someone actually needs to share a link.
 *
 * The first two follow the two levels of record in the store: a run's ranking is
 * immutable and per-run, the board is what every run so far agrees about a
 * target. Merging them into one table would have to pick one of those to show and
 * would quietly answer the other question wrong.
 *
 * Ask reads those same records through an agent instead of a table, which is why
 * it is here rather than a separate page: the questions it answers are about the
 * rows on the two tabs beside it, and it holds no state this file needs to know
 * about — its thread lives for as long as the tab is mounted.
 *
 * The Wiki is generated at build time from the same instruction files the agents
 * read, and fetches nothing — so it costs a tab and no request.
 *
 * Mostly read-only over the science. Two writes: the stop button in `RunHeader`,
 * which stops the *selected* run — deliberate, because the runs in the sidebar are
 * one click apart and a stop button per row invites killing the wrong sweep after
 * eight hours of work — and the per-target report request in `TargetTable`, which
 * is confirmed in a modal because it mails a confidential document.
 *
 * There is one query-string convention despite there being no router: a report
 * email links back here as `?run=<id>&report=<id>`, because a browser following a
 * link from an email carries no bearer token and cannot call the authenticated
 * download directly. This page holds the MSAL token, so the link selects the run
 * and offers the document.
 *
 * ── What this file is, and what it deliberately is not ──────────────────────────
 *
 * Four things only: auth gating, the report-email deep link, which tab is showing,
 * and where the shell and the two data tabs mount. The header, the run rail and both
 * tab bodies are their own components.
 *
 * That split is not tidiness. Every part of this UI is being rebuilt, and while it
 * was one 370-line file each of those changes was a change to *this* file, so they
 * could not proceed at the same time without colliding here. The parts now own whole
 * files. **Prefer not to add to this one**: a control that belongs to a tab belongs
 * in the tab, and the tab components take their whole hook result precisely so that
 * growing one does not come back here.
 *
 * The report notice is the exception and it stays. It is a receipt for an action that
 * can be started from either data tab, so it renders above the view switch; a receipt
 * that only appears on the tab you happened to be on when it arrived is no receipt.
 */
import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { useAuth } from './auth/useAuth'
import { useBoard } from './hooks/useBoard'
import { useLeaderboard } from './hooks/useLeaderboard'
import { useReports } from './hooks/useReports'
import { AppHeader, type View } from './components/AppHeader'
import { AskTab } from './components/AskTab'
import { BoardTab } from './components/BoardTab'
import { RunRail } from './components/RunRail'
import { RunTab } from './components/RunTab'
import { SignInGate } from './components/SignInGate'
import { Wiki } from './components/Wiki'
import { Button } from './components/ui/Button'
import { FailedState } from './components/ui/States'

/**
 * `?run=` / `?report=` from a report email, read once at module scope.
 *
 * Once, not per render, and not from state: the link is consumed the first time
 * the page loads and must not re-assert the selection every time the reader
 * clicks a different run in the sidebar. Read at module scope rather than in an
 * effect so it is the same object on both StrictMode passes.
 */
const LINK_PARAMS = new URLSearchParams(window.location.search)
const LINKED_RUN_ID = LINK_PARAMS.get('run')
const LINKED_REPORT_ID = Number(LINK_PARAMS.get('report')) || null

export default function App() {
  const auth = useAuth()
  const [view, setView] = useState<View>('run')

  // Gate fetching on `ready`: with auth on, firing requests before the redirect
  // callback has resolved sends them with no token and gets a 401, which would
  // show the sign-in gate to a user who is in fact signed in.
  const authed = auth.ready && (!auth.enabled || !!auth.account)
  const board = useLeaderboard(auth.getToken, authed)
  // Not fetched until the tab is opened: most visits only want the latest run,
  // and the board is a second request over the whole history.
  const cumulative = useBoard(auth.getToken, authed && view === 'board')
  // Reports follow the selected run, and the hook additionally tracks any run an
  // expanded board row registers — a board entry is cross-run, so its report is
  // filed against whichever run last ranked the target. Polled while any is queued
  // or running, and idle otherwise. Shared by both tables so one poll covers both.
  const reports = useReports(board.selectedRunId, auth.getToken, authed)

  // ── Follow a link from a report email ─────────────────────────────────────
  // Only while the linked run is not already selected, and only once: the landing
  // load picks the newest run that ranked something, which is usually not the one
  // the email is about. `selectRun` resets sort and page, which is correct here —
  // the reader has not chosen either yet.
  const [linkHandled, setLinkHandled] = useState(false)
  // Destructured out of `board` so the effect's dependencies are the three values
  // it actually reads. Calling `board.selectRun()` inside it makes the lint rule
  // demand the whole hook result, which changes on every render.
  const { runs, selectedRunId, selectRun } = board
  useEffect(() => {
    if (linkHandled || !authed || !LINKED_RUN_ID) return
    // Waits for the run list so a bad or deleted id in a link does not blank the
    // board — an unknown run simply never matches and the landing view stands.
    if (runs.length === 0) return
    setLinkHandled(true)
    if (runs.some((r) => r.run_id === LINKED_RUN_ID)) {
      setView('run')
      if (selectedRunId !== LINKED_RUN_ID) selectRun(LINKED_RUN_ID)
    }
  }, [linkHandled, authed, runs, selectedRunId, selectRun])

  // The report the email linked to, once its run's rows have loaded. Offered as a
  // banner rather than downloaded automatically: a download that starts on its own
  // is what browsers block, and the reader may have opened the link only to see
  // the ranking it came from.
  const linkedReport =
    LINKED_REPORT_ID !== null ? reports.byId.get(LINKED_REPORT_ID) : undefined

  if (!auth.ready) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-slate-400">
        Signing in…
      </div>
    )
  }

  const unauthorized = board.unauthorized || cumulative.unauthorized
  if (auth.enabled && (!auth.account || unauthorized)) {
    return (
      <SignInGate
        onSignIn={auth.login}
        // Signing in again is the wrong advice when the account is fine and the
        // tenant simply has not consented to the API scope — that loops the user
        // through Entra forever. `consentRequired` splits the two.
        consentRequired={!!auth.account && unauthorized && auth.consentRequired}
        error={
          auth.error ??
          (unauthorized ? 'Your session has expired. Sign in again.' : null)
        }
      />
    )
  }

  // Null on Wiki, which fetches nothing, and on Ask, which owns its own failures:
  // a leaderboard error banner over a page built at compile time — or over a
  // chat turn that failed for its own reasons — would blame the wrong thing. The
  // sidebar's run list still surfaces its own failure to load.
  const mainError =
    view === 'run' ? board.error : view === 'board' ? cumulative.error : null

  // Entra puts the UPN in `username`. Empty with auth disabled, which is also the
  // case where the API refuses a report request — the modal says "your signed-in
  // address" and the 403 explains why there isn't one.
  const accountEmail = auth.account?.username ?? ''

  return (
    <div className="flex h-full flex-col">
      <AppHeader
        view={view}
        onViewChange={setView}
        onRefresh={board.refresh}
        refreshing={board.loading}
        accountName={auth.account?.username ?? null}
        onSignOut={auth.enabled && auth.account ? auth.logout : null}
      />

      <div className="flex min-h-0 flex-1">
        <RunRail
          runs={board.runs}
          // The rail's own three states, not the main panel's. `board.error` is
          // shared by the landing fetch and the paged one, so it is only the run
          // list's failure while `runsLoaded` is still false — after that a failed
          // page fetch belongs to the table, and the rail keeps its rows.
          runsLoaded={board.runsLoaded}
          runsFailed={!board.runsLoaded && !!board.error}
          selectedRunId={board.selectedRunId}
          onSelect={board.selectRun}
          onDelete={board.removeRun}
          deleting={board.deleting}
          deleteError={board.deleteError}
        />

        <main className="min-w-0 flex-1 space-y-4 overflow-y-auto p-5">
          {mainError && (
            <FailedState
              message={mainError}
              // Retry only on the run tab, which is the only one of the two hooks
              // that exposes a refetch. Offering a dead button on the board would
              // be worse than offering none.
              onRetry={view === 'run' ? board.refresh : undefined}
            />
          )}

          {/* The API's own sentence about the last report request: it names the
              target and the address it will go to, which is the receipt for an
              action whose result arrives by email minutes later. Above the view
              switch, not inside the run tab: a report can now be requested from the
              board as well, and a receipt that only renders on the other tab is no
              receipt. */}
          {reports.notice && (
            <div className="flex items-start justify-between gap-3 rounded border border-brand-800 bg-brand-900/30 p-3 text-sm text-brand-200">
              <p>{reports.notice}</p>
              <Button
                variant="ghost"
                size="icon"
                onClick={reports.dismissNotice}
                aria-label="Dismiss"
                className="-my-1 h-7 w-7 shrink-0 text-brand-300 hover:bg-brand-900/60 hover:text-brand-100"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}

          {view === 'wiki' ? (
            <Wiki />
          ) : view === 'ask' ? (
            // `getToken` rather than the token: MSAL refreshes silently, and a
            // string captured at render is stale by the second question of a long
            // conversation.
            <AskTab getToken={auth.getToken} />
          ) : view === 'board' ? (
            <BoardTab
              cumulative={cumulative}
              reports={reports}
              accountEmail={accountEmail}
              getToken={auth.getToken}
            />
          ) : (
            <RunTab
              board={board}
              reports={reports}
              accountEmail={accountEmail}
              linkedReport={linkedReport}
            />
          )}
        </main>
      </div>
    </div>
  )
}
